import { DASH } from '../utils/displayValues';
import { DocumentTemplateShell, TemplateEmptyState } from './TemplateShared';
import type { TemplatePatient } from './templateUtils';

export interface ProvidersTemplateRow {
  id?: string;
  name?: string | null;
  npi?: string;
  providerId?: string;
  specialty?: string;
  address?: string;
  phoneNumber?: string;
  faxNumber?: string;
  email?: string;
  telephones?: string[];
}

export interface ProvidersTemplateProps {
  rows: ProvidersTemplateRow[];
  patient?: TemplatePatient;
  filename?: string;
  generatedAt?: Date;
  pageInfo?: { current: number; total: number };
  highlightRowId?: string;
}

const headers = [
  'Name',
  'NPI',
  'Provider ID',
  'Specialty',
  'Address',
  'Contact',
] as const;

const headerCellClass =
  'flex min-w-0 flex-1 items-start border-r border-b border-zinc-300 bg-slate-100 px-2 py-3';

const bodyCellClass =
  'flex min-w-0 flex-1 items-start border-r border-t border-zinc-200 px-2 py-2.5';

const displayCellValue = (value?: string | null) =>
  value?.toString().trim() ? value : DASH;

export const formatProviderContact = (
  row: ProvidersTemplateRow,
): string | undefined => {
  const lines: string[] = [];
  const primaryPhone =
    row.phoneNumber?.trim() || row.telephones?.[0]?.trim() || undefined;
  const fax = row.faxNumber?.trim();
  const email = row.email?.trim();
  const extraTelephone = row.telephones
    ?.map((telephone) => telephone.trim())
    .find(
      (telephone) =>
        telephone && telephone !== primaryPhone && telephone !== fax,
    );

  if (primaryPhone) lines.push(`P: ${primaryPhone}`);
  if (fax) lines.push(`F: ${fax}`);
  if (email) lines.push(`E: ${email}`);
  if (extraTelephone) lines.push(`T: ${extraTelephone}`);

  return lines.length > 0 ? lines.join('\n') : undefined;
};

export const ProvidersTemplate = ({
  rows,
  patient,
  filename,
  generatedAt,
  pageInfo,
  highlightRowId,
}: ProvidersTemplateProps) => {
  const items = rows ?? [];

  return (
    <DocumentTemplateShell
      title="Providers Report"
      titleClassName="text-[22px] leading-tight font-bold text-black"
      patient={patient}
      filename={filename}
      generatedAt={generatedAt}
      pageInfo={pageInfo}
    >
      <section className="flex flex-1 flex-col gap-4 px-6 py-4">
        {items.length === 0 ? (
          <div className="px-4">
            <TemplateEmptyState message="No providers available" />
          </div>
        ) : (
          <div className="px-4">
            <div
              className="overflow-hidden rounded-sm border border-zinc-200"
              data-testid="providers-table"
            >
              <div className="flex" data-testid="providers-table-header-row">
                {headers.map((header) => (
                  <div key={header} className={headerCellClass}>
                    <p className="min-w-0 flex-1 text-[9px] leading-tight font-black tracking-[0.35px] text-zinc-500 uppercase">
                      {header}
                    </p>
                  </div>
                ))}
              </div>

              {items.map((row, rowIdx) => {
                const isHighlighted =
                  !!highlightRowId && row.id === highlightRowId;
                const zebra = rowIdx % 2 === 1 ? 'bg-gray-50' : 'bg-white';
                const rowBg = isHighlighted ? 'bg-amber-100' : zebra;
                const cells = [
                  row.name,
                  row.npi,
                  row.providerId,
                  row.specialty,
                  row.address,
                  formatProviderContact(row),
                ];
                const rowKey = row.id ?? `${row.name ?? 'provider'}-${rowIdx}`;

                return (
                  <div
                    key={rowKey}
                    id={row.id ? `provider-${row.id}` : undefined}
                    tabIndex={isHighlighted ? -1 : undefined}
                    data-testid={`providers-table-body-row-${rowKey}`}
                    className="flex"
                  >
                    {cells.map((cell, colIdx) => (
                      <div
                        key={headers[colIdx]}
                        className={`${bodyCellClass} ${rowBg}`}
                      >
                        <p className="min-w-0 flex-1 text-[10px] leading-tight break-words whitespace-pre-line text-zinc-950">
                          {displayCellValue(cell)}
                        </p>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>
    </DocumentTemplateShell>
  );
};
